import _ from "lodash";
import async from "async";
import {basicContext} from "../../utils/contextUtils";
import {GET_OBJECT_SUCCESS} from "../../../apps/KpModule/actions/api";
import {setFieldEdition} from "../../../apps/KpModule/actions";
import {newOrganizationActivities} from "./staticEntities";

const activityIds = newOrganizationActivities.map(a => a.id)
const emptyActivityFields = () => newOrganizationActivities.reduce((acc, a) => ({ ...acc, [a.id]: 0 }), {})

async function findShelters(context, callback) {
    try {
        const exerciseQuery = !!context.data.exercises?.length
            ? {_id: {$in: context.data.exercises.map(exercise => new global.ObjectID(exercise.id))}}
            : {}
        const exercises = await global.app.C.Exercise.find({
            ...basicContext(context),
            fieldPath: ['id', 'code'],
            query: exerciseQuery
        })
        if(!exercises.length) return callback(null, [])

        const user = context.user
        const cUser = await global.app.C.CUser.get({kpUser: new global.ObjectID(user.id)}, {
            ...basicContext(context),
            fieldPath: ['id', 'organization.id', 'organization.organizationType.id', 'organization.status.id', 'shelters.id'],
            ignoreNotFound: true
        })

        const userOrgIsMemberAssociation = cUser?.organization?.organizationType?.id === 'association' && cUser.organization?.status?.id === 'member'
        const isAdministratorModule = context.clientContext.moduleId === 'm-C-capacityAdministratorModule'

        if(!isAdministratorModule && !userOrgIsMemberAssociation) return callback(null, [])

        const associationOrganizations = isAdministratorModule
            ? await global.app.C.Organization.find({
                ...basicContext(context),
                fieldPath: ['id'],
                query: {organizationType: 'association'}
            })
            : []

        const shelters = await global.app.C.Shelter.find({
            ...basicContext(context),
            fieldPath: ['id', 'name', 'organization.id', 'organization.name', 'organization.tradingName', 'animalTypes.id', 'animalTypes.name'],
            query: isAdministratorModule
                ? {organization: {$in: associationOrganizations.map(organization => new global.ObjectID(organization.id))}}
                : !!cUser.shelters.length
                    ? {_id: {$in: cUser.shelters.map(shelter => new global.ObjectID(shelter.id))}}
                    : {organization: new global.ObjectID(cUser.organization.id)}
        })

        const pairs = exercises.flatMap(exercise => shelters.map(shelter => ({exercise, shelter})))

        async.map(pairs, ({exercise, shelter}, callback) => {
            const animalTypeQuery = !!context.data.animalTypes?.length
                ? {animalType: {$in: context.data.animalTypes.map(animalType => new global.ObjectID(animalType.id))}}
                : {}
            global.app.C.Capacity.find({
                ...basicContext(context),
                fieldPath: ['id', 'animalType.id', 'animalType.name', 'ddpp', 'totalCapacity', ...activityIds],
                query: {
                    shelter: new global.ObjectID(shelter.id),
                    exercise: new global.ObjectID(exercise.id),
                    ...animalTypeQuery
                }
            }, (e, capacities) => {
                return callback(e, capacities.reduce((acc, capacityDetails) => {
                    return {
                        ...acc,
                        ddpp: acc.ddpp + (parseInt(capacityDetails.ddpp, 10) || 0),
                        totalCapacity: acc.totalCapacity + (parseInt(capacityDetails.totalCapacity, 10) || 0),
                        ...newOrganizationActivities.reduce((aAcc, a) => ({
                            ...aAcc,
                            [a.id]: acc[a.id] + (parseInt(capacityDetails[a.id], 10) || 0)
                        }), {})
                    }},
                    {
                        id: `${exercise.id}_${shelter.id}`,
                        shelter: _.pick(shelter, ['id', 'name']),
                        exercise,
                        organization: shelter.organization,
                        animalTypesNames: (!!context.data.animalTypes?.length
                            ? shelter.animalTypes.filter(at => context.data.animalTypes.some(fat => fat.id === at.id))
                            : shelter.animalTypes
                        ).map(at => at.name).join(', '),
                        ddpp: 0,
                        totalCapacity: 0,
                        ...emptyActivityFields()
                    }
                ))
            })
        }, callback)
    } catch (e) {
        callback(e)
    }

}
async function getShelterDetails(id, context) {
    const [exerciseId, shelterId] = id.split('_')

    const exercise = await global.app.C.Exercise.get(exerciseId, {
        ...basicContext(context),
        fieldPath: ['id', 'code'],
    })

    const shelter = await global.app.C.Shelter.get(shelterId, {
        ...basicContext(context),
        fieldPath: ['id', 'name', 'organization.id', 'organization.name', 'organization.tradingName', 'animalTypes.id', 'animalTypes.name', 'types.id', 'types.name'],
    })
    const capacities = await global.app.C.Capacity.find({
        ...basicContext(context),
        fieldPath: ['id', 'animalType.id', 'animalType.name', 'ddpp', 'totalCapacity', ...activityIds],
        query: {
            shelter: new global.ObjectID(shelter.id),
            exercise: new global.ObjectID(exercise.id)
        }
    })

    return {
        id: `${exercise.id}_${shelter.id}`,
        organization: shelter.organization,
        exercise,
        shelter: _.pick(shelter, ['id', 'name', 'animalTypes', 'types']),
        capacityByAnimalType: shelter.animalTypes.map(animalType => {
            const capacityDetails = capacities.find(capacity => capacity.animalType.id === animalType.id)
            const capacityByActivity = (shelter.types || []).reduce((acc, type) => ({
                ...acc,
                [type.name]: capacityDetails ? capacityDetails[type.id] || 0 : 0
            }), {})
            return {
                id: capacityDetails ? capacityDetails.id : new global.ObjectID(),
                animalType,
                ddpp: capacityDetails ? capacityDetails.ddpp : 0,
                totalCapacity: capacityDetails ? capacityDetails.totalCapacity : 0,
                capacityByActivity
            }
        })
    }
}

function saveShelterDetails(newObject, context, callback) {
    const animalTypes = _.get(context, 'data.animalTypes', []) || []
    const options = { upsert: true }
    const shelterTypes = newObject.shelter.types || []
    const getActivityValue = (capacityByActivity, activityId) => {
        const type = shelterTypes.find(t => t.id === activityId)
        return type ? Number(capacityByActivity[type.name]) || 0 : 0
    }
    const queries = newObject.capacityByAnimalType.map(animalTypeCapacity => {
        const capacityByActivity = animalTypeCapacity.capacityByActivity || {}
        const activityUpdates = newOrganizationActivities.reduce((acc, activity) => ({
            ...acc,
            [activity.id]: getActivityValue(capacityByActivity, activity.id)
        }), {})
        return {
            query: {
                exercise: new global.ObjectID(newObject.exercise.id),
                shelter: new global.ObjectID(newObject.shelter.id),
                animalType: new global.ObjectID(animalTypeCapacity.animalType.id),
                group: new global.ObjectID(context.group.id)
            },
            update: {
                $set : {
                    exercise: new global.ObjectID(newObject.exercise.id),
                    shelter: new global.ObjectID(newObject.shelter.id),
                    animalType: new global.ObjectID(animalTypeCapacity.animalType.id),
                    ddpp: animalTypeCapacity.ddpp,
                    totalCapacity: animalTypeCapacity.totalCapacity,
                    ...activityUpdates,
                    group: new global.ObjectID(context.group.id)
                }
            }
        }
    })
    async.parallel(
        queries.map(query => callback =>
            global.db.collection("c.capacity")
                .updateOne(query.query, query.update, options)
                .then(() => callback())
                .catch(callback)
        ), error => callback(
            error,
            newObject.capacityByAnimalType
                .filter(capacityDetails => {
                    const animalTypeId = capacityDetails.animalType?.id
                    return animalTypeId && ( !animalTypes.length || animalTypes.some(at => at.id === animalTypeId))
                })
                .reduce((acc, capacityDetails) => {
                    return {
                        ...acc,
                        ddpp: acc.ddpp + capacityDetails.ddpp,
                        totalCapacity: acc.totalCapacity + capacityDetails.totalCapacity,
                        ...newOrganizationActivities.reduce((aAcc, a) => ({
                            ...aAcc,
                            [a.id]: acc[a.id] + getActivityValue(capacityDetails.capacityByActivity || {}, a.id)
                        }), {})
                    }
                },
                    {
                        id: newObject.id,
                        shelter: newObject.shelter,
                        organization: newObject.organization,
                        exercise: newObject.exercise,
                        animalTypesNames: (!!animalTypes?.length
                                ? newObject.shelter.animalTypes.filter(at => animalTypes.some(fat => fat.id === at.id))
                                : newObject.shelter.animalTypes
                        ).map(at => at.name).join(', '),
                        ddpp: 0,
                        totalCapacity: 0,
                        ...emptyActivityFields()
                    }
                )
        )
    )
}

export const entities = [
    {
        name: 'Capacity',
        fields: [
            'Exercise',
            'Shelter',
            'AnimalType',
            {path: 'ddpp', type: 'integer'},
            {path: 'totalCapacity', type: 'integer'},
            ...newOrganizationActivities.map(a => ({path: a.id, type: 'integer'}))
        ]
    },
    {
        name: "CapacityByAnimalType",
        type: "mongoInternal",
        fields: [
            "AnimalType",
            {path: 'ddpp', type: 'integer'},
            {path: 'totalCapacity', type: 'integer'},
            ...newOrganizationActivities.map(a => ({path: a.id, type: 'integer'}))
        ]
    },
    {
        name: 'CapacityByShelter',
        fields: [
            "Organization",
            "Shelter",
            "Exercise",
            {path: "capacityByAnimalType", type: "CapacityByAnimalType", link: "MTO"}
        ],
        find: function(context, callback) {
            this.prepareContext(context, 'L', (error, context) => {
                if (error) return callback(error);

                else {
                    try {
                        findShelters(context, callback)
                    } catch (e) {
                        callback(e)
                    }
                }
            })
        },
        get: function(id, context, callback) {
            this.prepareContext(context, 'R', (error, context) => {
                if (error) callback(error);
                else getShelterDetails(id, context, callback)
                    .then(object => callback(null, object))
                    .catch(e => callback(e))
            })
        },
        save: function(newObject, context, callback) {
            this.prepareContext(context, 'S', (error, context) => {
                if (error) callback(error)
                else saveShelterDetails(newObject, context, callback)
            })
        }
    }
]


export const administratorModule = {
    name: 'CapacityAdministratorModule',
    object: 'CapacityByShelter',
    tKey: 'mTitle_capacity',
    objectIdentifier: 'name',
    newable: false,
    removable: false,
    category: {
        path: 'menuTitle_phoneBook',
        icon: 'notebook'
    },
    viewMap: {
        dt: [
            {path: 'organization', display: 'tradingName'}, 'shelter', {path: 'exercise', display: 'code'},
            {path: 'animalTypesNames', tKey: 'espèces'},
            {path: 'ddpp', tKey: 'Capacité agréée DDPP'},
            {path: 'totalCapacity', tKey: 'Capacité réelle'},
            ...newOrganizationActivities.map(a => ({path: a.id, tKey: a.name}))
        ],
        form: [
            {path: 'organization', display: 'tradingName', editable: false},
            {path: 'exercise', display: 'code', fieldPath: ['id', 'code', 'exerciseStatus.id'], editable: false},
            {path: 'shelter', editable: false, fieldPath: ['id', 'name', 'animalTypes.id', 'animalTypes.name', 'types.id', 'types.name']},
            {
                path: 'capacityByAnimalType',
                tKey: 'Chiffres produits lors de la dernière assemblée générale',
                type: 'dtObjects',
                fields: [
                    'animalType',
                    {path: 'ddpp', tKey: 'Capacité agréée DDPP', type: 'editText', width: 100},
                    {path: 'totalCapacity', tKey: 'Capacité réelle', type: 'editText', width: 100},
                    {path: 'capacityByActivity', type: 'editText', dynamic: true}
                ]
            }
        ]
    },
    actionSubscriptions: [
        {
            actionType: GET_OBJECT_SUCCESS,
            subscription: ({ store }) => {
                const state = store.getState()

                const exercise = _.get(state, 'edit.object.data.exercise')

                store.dispatch(setFieldEdition('e_capacityByAnimalType', _.get(exercise, 'exerciseStatus.id') !== 'finalClosure'))
            }
        }
    ],
    filters: [
        {
            path: 'exercises',
            object: 'Exercise',
            type: 'tags',
            display: 'code',
            client: true
        },
        {
            path: 'animalTypes',
            tKey: 'les espèces prises en compte dans le calcul',
            object: 'AnimalType',
            type: 'tags',
            display: 'name',
            client: true,
            default: []
        }
    ],
}

export const userModule = {
    name: 'CapacityUserModule',
    object: 'CapacityByShelter',
    tKey: 'mTitle_capacity',
    objectIdentifier: 'name',
    newable: false,
    removable: false,
    category: {
        path: 'menuTitle_myAssociation',
        icon: 'building'
    },
    viewMap: {
        dt: [
            {path: 'organization', display: 'tradingName'}, 'shelter', {path: 'exercise', display: 'code'},
            {path: 'animalTypesNames', tKey: 'espèces'},
            {path: 'ddpp', tKey: 'Capacité agréée DDPP'},
            {path: 'totalCapacity', tKey: 'Capacité réelle'},
            ...newOrganizationActivities.map(a => ({path: a.id, tKey: a.name}))
        ],
        form: [
            {path: 'organization', display: 'tradingName', editable: false},
            {path: 'exercise', display: 'code', fieldPath: ['id', 'code', 'exerciseStatus.id'], editable: false},
            {path: 'shelter', editable: false, fieldPath: ['id', 'name', 'animalTypes.id', 'animalTypes.name', 'types.id', 'types.name']},
            {
                path: 'capacityByAnimalType',
                tKey: 'Chiffres produits lors de la dernière assemblée générale',
                type: 'dtObjects',
                fields: [
                    'animalType',
                    {path: 'ddpp', tKey: 'Capacité agréée DDPP', type: 'editText', width: 100},
                    {path: 'totalCapacity', tKey: 'Capacité réelle', type: 'editText', width: 100},
                    {path: 'capacityByActivity', type: 'editText', dynamic: true}
                ]
            }
        ]
    },
    actionSubscriptions: [
        {
            actionType: GET_OBJECT_SUCCESS,
            subscription: ({ store }) => {
                const state = store.getState()

                const exercise = _.get(state, 'edit.object.data.exercise')

                store.dispatch(setFieldEdition('e_capacityByAnimalType', _.get(exercise, 'exerciseStatus.id') !== 'finalClosure'))
            }
        }
    ],
    filters: [
        {
            path: 'exercises',
            object: 'Exercise',
            type: 'tags',
            display: 'code',
            client: true
        },
        {
            path: 'animalTypes',
            tKey: 'les espèces prises en compte dans le calcul',
            object: 'AnimalType',
            type: 'tags',
            display: 'name',
            client: true,
            default: []
        }
    ],
}
